/**
 * Розміри комірок і вільні місця за розмірами — спільне для ОБОХ реалізацій
 * поштоматного флоу (`app_min1.html` + `app.js` і `courier.html` +
 * `courier/sections/locker.js`).
 *
 * Чому окремий листковий модуль. Дві реалізації вже розійшлись копіями
 * `CELL_SIZES`, `mapCellSizeForApi` і `nextSizeForAgent` — і саме через це та
 * сама дія курʼєра на двох екранах давала різний `CellSize` у 1С. Той самий
 * прийом, що й `locker-marks.js`: жодних імпортів (ні ui.js, ні api.js), щоб
 * модуль однаково імпортувався і з `flows.js`, і з courier-екрана з його
 * власними core-модулями.
 *
 * Головне правило цього файлу: дані ПІДСИЛЮЮТЬ вибір курʼєра, а не замінюють
 * його. Габарити посилки бачить лише курʼєр. Тому «не знаю, скільки вільно»
 * ніколи не гасить кнопку — гасить лише явний нуль від 1С.
 */

/** Порядок зростання. Він же — порядок кнопок на екрані. */
export const CELL_SIZES = ['XS', 'S', 'M', 'L', 'XL'];

/** Розмір, з якого починається підбір, коли курʼєр не обрав сам. */
export const DEFAULT_CELL_SIZE = 'S';

/** Код 1С «Немає вільних комірок» — найчастіша відмова на проді. */
export const LOCKER_CODE_NO_FREE_CELLS = '20';
// 25 — 1С з 16.10: поштомат не підтвердив відкриття (відвалився звʼязок). Бронь
// 1С НЕ скасовує (команда CREATED/PENDING) — комірка може відкритись пізніше.
export const LOCKER_CODE_NOT_OPENED = '25';
// 13 — у 1С «внутрішня помилка»: стек-трейс модуля («{ОбщийМодуль.МодульАПИ…}:
// Значення не є значенням об'єктного типу»), «EPIC: расхождение остатков», а з
// 21.10 — ще й відмова змінити розмір броні Епіцентру (див. isCellResizeRejected).
export const LOCKER_CODE_1C_FAILURE = '13';

/**
 * Відмова 1С змінити розмір броні Епіцентру (з 21.10.2026). На посилку агента
 * «Епіцентр онлайн_поштомати» вже є бронь комірки (найчастіше XS — передбронь
 * Епіцентру або наша попередня спроба), курʼєр обрав інший розмір — 1С кличе
 * зміну розміру броні в Епіцентрі й віддає 13 «Не вдалося змінити розмір комірки
 * Epicentr. HTTP 204|400|418». Розмір наявної броні при цьому відкривається.
 * Окремого коду 1С не дала, тому ознака — код 13 і слова в описі (регістр і
 * нерозривні пробіли не важать).
 */
export function isCellResizeRejected(code, details) {
  if (String(code ?? '').trim() !== LOCKER_CODE_1C_FAILURE) return false;
  const text = String(details ?? '').replace(/\s+/g, ' ').toLowerCase();
  return text.includes('змінити розмір') || text.includes('изменить размер');
}

/**
 * Чи пробувати після відмови 1С наступний розмір автоматично.
 * Ні — на 25 (поштомат не на звʼязку: інший розмір дасть те саме, а кожна
 * спроба — ще одна бронь у 1С) і на 13 (рішення власника 26.10): і збій 1С, і
 * відмову змінити розмір броні перебір S→M→L→XL лише множив — до 4 запитів на
 * тап і до 61 на посилку. Розмір після них обирає курʼєр. Решта відмов (20
 * «немає вільних» тощо) — як були.
 */
export function allowsAutoSizeEscalation(code) {
  const key = String(code ?? '').trim();
  return key !== LOCKER_CODE_NOT_OPENED && key !== LOCKER_CODE_1C_FAILURE;
}

// Тексти коду 13 для курʼєра — одні на обидва екрани (як epFirstText).
// На ВІДКРИТТІ порада «відкрийте ще раз» свідомо відсутня: повтор того самого
// запиту на 13 рідко щось змінює, а курʼєри тиснули його десятки разів.
export const LOCKER_TEXT_1C_FAILURE =
  'Поштомат не прийняв запит через збій 1С. Спробуйте ще раз за хвилину, а якщо повториться — натисніть «Проблема»';
// На ПІДТВЕРДЖЕННІ 13 — це здебільшого відхилений transID/reservationID: повторне
// «Підтвердити» шле той самий і отримує ту саму відмову, а свіжий дає лише нове
// відкриття. Тому тут лишається текст, що був до 26.10, — це ручна дія курʼєра,
// перебору розмірів вона не запускає.
export const LOCKER_TEXT_1C_FAILURE_CONFIRM =
  'Поштомат не прийняв запит через збій 1С. Відкрийте комірку ще раз, а якщо повториться — натисніть «Проблема»';
/** Що сталось — заголовок плашки app_min1 (і початок повного тексту). */
export const CELL_RESIZE_REJECTED_TITLE = 'Не вдалося змінити розмір броні';
/**
 * Тип «Проблеми» саме для цього випадку — назва з довідника типів
 * (problem_templates, `epicenter_swap_failed`, так само на проді). На ньому
 * тримається аналітика «не міняється комірка» і Reason для 1С; «Комірка не
 * відкривається» виглядала б як збій заліза.
 */
export const EPICENTR_SWAP_PROBLEM_TITLE = 'Не міняється комірка (Епіцентр)';

/**
 * Памʼять відмов 13 «змінити розмір» для ОДНІЄЇ посилки: які розміри 1С для неї
 * вже відхилила. Потрібна, щоб «Відкрити повторно» не слало відхилений розмір
 * ще раз (за 21–25.10 на проді повтор того самого розміру протягом години
 * спрацював 6 разів із ~1 600) і щоб порада не радила щойно відхилений розмір.
 * Інша посилка — памʼять порожня; значення незмінне (новий обʼєкт).
 */
export function rememberCellResizeRejected(memo, parcelID, size) {
  const key = String(size ?? '').trim().toUpperCase();
  if (!parcelID || !key) return memo || null;
  const sizes = cellResizeRejectedSizes(memo, parcelID);
  return { parcelID, sizes: sizes.includes(key) ? sizes : [...sizes, key] };
}

/** Розміри, які 1С відхилила для цієї посилки (порожньо, якщо посилка інша). */
export function cellResizeRejectedSizes(memo, parcelID) {
  if (!memo || !parcelID || memo.parcelID !== parcelID || !Array.isArray(memo.sizes)) return [];
  return memo.sizes.slice();
}

/**
 * Розміри, які після відмови ще варто пробувати: не відхилені для цієї
 * посилки і не погашені в сітці (isSizeBlocked — їх курʼєр і не натисне).
 */
export function cellResizeCandidates(rejected, view) {
  const done = Array.isArray(rejected) ? rejected : [];
  return CELL_SIZES.filter((s) => !done.includes(s) && !isSizeBlocked(view, s));
}

/**
 * Куди поставити фокус після відмови: XS, якщо його ще можна спробувати
 * (за 21–25.10 це половина відкриттів у межах 30 хв після такої відмови),
 * інакше перший доступний. `null` — пробувати нічого.
 */
export function suggestCellResizeSize(rejected, view) {
  const candidates = cellResizeCandidates(rejected, view);
  if (candidates.includes('XS')) return 'XS';
  return candidates[0] || null;
}

/**
 * Що робити — тіло плашки app_min1 і тосту courier.html після 13 «змінити
 * розмір», під конкретну посилку: називає відхилені розміри (їх не повторювати),
 * радить XS лише тоді, коли його ще можна натиснути і його не відхилено, і
 * веде в «Проблему» з потрібним типом. Без розмірів (rejected порожній) —
 * загальний варіант.
 */
export function cellResizeRejectedHint(rejected, view) {
  const done = CELL_SIZES.filter((s) => (Array.isArray(rejected) ? rejected : []).includes(s));
  const candidates = cellResizeCandidates(done, view);
  let skip = 'той самий розмір не повторюйте';
  if (done.length === 1) skip = `розмір ${done[0]} не повторюйте`;
  else if (done.length > 1) skip = `розміри ${done.join(', ')} не повторюйте`;
  const problem = `«Проблема» → «${EPICENTR_SWAP_PROBLEM_TITLE}»`;
  const what = `Для цієї посилки в Епіцентрі вже є бронь комірки іншого розміру — ${skip}.`;
  if (!candidates.length) return `${what} Інших розмірів для спроби немає: натисніть ${problem}.`;
  const pick = candidates.includes('XS')
    ? 'Оберіть інший: найчастіше відкривається XS або той, що відкривався раніше.'
    : 'Оберіть інший — найімовірніше той, що відкривався раніше.';
  return `${what} ${pick} Не відкрився жоден — ${problem}.`;
}

/** Загальний варіант поради (без розмірів) — мапа текстів app.js і журнал. */
export const CELL_RESIZE_REJECTED_HINT = cellResizeRejectedHint([], null);
/** Повний текст для тосту (courier.html): що сталось + що робити. */
export const CELL_RESIZE_REJECTED_TEXT = `${CELL_RESIZE_REJECTED_TITLE}. ${CELL_RESIZE_REJECTED_HINT}`;
/** Повний текст із порадою під посилку (courier.html, тост). */
export function cellResizeRejectedText(rejected, view) {
  return `${CELL_RESIZE_REJECTED_TITLE}. ${cellResizeRejectedHint(rejected, view)}`;
}

/** «Відкрити повторно» з розміром, який 1С для цієї посилки вже відхилила. */
export function cellResizeRetryBlockedText(size) {
  return `Розмір ${size} для цієї посилки не прийнято: в Епіцентрі вже є бронь іншого розміру. Оберіть інший розмір у сітці або натисніть «Проблема»`;
}

/**
 * Мінімальний проміжок між РУЧНИМИ оновленнями довідки (мс).
 *
 * Автоматично метод викликається рівно один раз за візит — після сканування QR.
 * Ручне оновлення (тап по чипу) лишаємо, але не частіше, ніж раз на пів
 * хвилини: подвійний тик не має коштувати зайвого запиту в 1С.
 */
export const FREE_CELLS_MANUAL_REFRESH_MS = 30_000;

/**
 * Стани, які для цього поштомата вже не зміняться: метод його не вміє
 * (`unsupported_postamat` — наш Meest, Rozetka) або такого ref у 1С немає.
 * Перепитувати їх удруге за візит — марно витрачати бюджет викликів.
 */
const TERMINAL_STATUSES = ['unsupported_postamat', 'postamat_not_found'];

export function isTerminalFreeCellsStatus(status) {
  return TERMINAL_STATUSES.includes(String(status || ''));
}

/**
 * 1С сама каже, що звʼязку з поштоматом немає (`no_data` + `postamat_offline`,
 * з 19.10.2026 для VNA(new)). Кількостей тоді немає, але це знання про сам
 * поштомат — екран може перевірити звʼязок, не чекаючи помилки 25.
 */
export function isFreeCellsOffline(freeCells) {
  return Boolean(freeCells && !freeCells.available && freeCells.reason === 'postamat_offline');
}

export function nextCellSize(sz) {
  const i = CELL_SIZES.indexOf(sz);
  if (i < 0 || i === CELL_SIZES.length - 1) return null;
  return CELL_SIZES[i + 1];
}

/**
 * Наступний розмір для спроби. `null` — більшого немає.
 *
 * Коли дані про вільні комірки Є, ми:
 *  • не тримаємо епіцентрівську стелю на L — тепер відомо, чого справді
 *    вільно, а метод відкриття приймає XL;
 *  • перестрибуємо розміри з явним нулем: кожна така спроба — це гарантована
 *    відмова «Немає вільних комірок» (20 336 таких за 14 днів на проді).
 *
 * Коли даних НЕМАЄ — поведінка рівно та, що була до 1.9.1, включно з
 * епіцентрівською стелею на L. Це запобіжник: без даних ми не знаємо, чи
 * приймає цей поштомат XL узагалі.
 */
export function nextSizeForSelection({ size, isEpicentr = false, freeCells = null } = {}) {
  const from = CELL_SIZES.indexOf(size);
  if (from < 0) return null;
  const hasData = Boolean(freeCells && freeCells.available);

  if (!hasData) {
    if (isEpicentr) {
      // Стеля на L: історична поведінка епіцентрівських посилок.
      const capped = CELL_SIZES.indexOf('L');
      if (from >= capped) return null;
      return CELL_SIZES[from + 1];
    }
    return nextCellSize(size);
  }

  // Після забору нулі зі знімка вже не знання (див. markFreeCellsStale), тож
  // перестрибувати через них не можна: інакше ескалація впреться в null на
  // поштоматі, у якому курʼєр сам щойно звільнив комірки.
  const trustZeros = !freeCells.stale;

  for (let i = from + 1; i < CELL_SIZES.length; i += 1) {
    const candidate = CELL_SIZES[i];
    const info = freeCells.bySize ? freeCells.bySize[candidate] : null;
    // Такого розміру в поштоматі фізично немає (VNA(new), Rozetka) — забір
    // цього не змінює, тож перестрибуємо його завжди.
    if (isAbsentSize(info)) continue;
    // Розмір без даних лишається кандидатом: «не знаю» ≠ «немає».
    if (trustZeros && info && typeof info.free === 'number' && info.free <= 0) continue;
    return candidate;
  }
  return null;
}

/**
 * Відповідь `/webapp/locker/free-cells` → форма, у якій це живе в state.
 * Будь-що несподіване зводиться до «даних немає».
 */
export function normalizeFreeCells(payload, now = Date.now()) {
  const empty = { available: false, status: 'unavailable', reason: null, actualOn: null, postamatType: null, bySize: {}, stale: false, fetchedAt: now };
  if (!payload || typeof payload !== 'object') return empty;
  const status = typeof payload.status === 'string' && payload.status ? payload.status : 'unavailable';
  // Пояснення до `no_data` (`postamat_offline`) — див. isFreeCellsOffline.
  const reason = typeof payload.reason === 'string' && payload.reason ? payload.reason : null;
  if (!payload.available || !Array.isArray(payload.cells) || !payload.cells.length) {
    return { ...empty, status, reason };
  }
  const bySize = {};
  payload.cells.forEach((cell) => {
    if (!cell || typeof cell !== 'object') return;
    const size = typeof cell.size === 'string' ? cell.size.trim().toUpperCase() : '';
    if (!CELL_SIZES.includes(size)) return;
    const free = Number(cell.free);
    if (!Number.isFinite(free)) return;
    bySize[size] = {
      free: Math.max(0, Math.trunc(free)),
      // Скільки комірок цього розміру всього (VNA(new), Rozetka); null — невідомо
      // (EPIC), 0 — такого розміру в поштоматі фізично немає.
      total: countOrNull(cell.total),
      height: numberOrNull(cell.height),
      width: numberOrNull(cell.width),
      depth: numberOrNull(cell.depth),
    };
  });
  if (!Object.keys(bySize).length) return { ...empty, status };
  return {
    available: true,
    status,
    reason,
    actualOn: typeof payload.actualOn === 'string' && payload.actualOn ? payload.actualOn : null,
    postamatType: typeof payload.postamatType === 'string' && payload.postamatType ? payload.postamatType : null,
    bySize,
    // Свіжа відповідь від 1С — застарілою вона стане лише після забору.
    stale: false,
    fetchedAt: now,
  };
}

function numberOrNull(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function countOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? Math.trunc(num) : null;
}

/** Розміру в поштоматі фізично немає: 1С назвала склад, і його там нуль. */
function isAbsentSize(info) {
  return Boolean(info && info.total === 0);
}

/** «1 вільна», «3 вільні», «10 вільних» — інакше підпис читається як машинний. */
export function freeCellsPlural(n) {
  const abs = Math.abs(Math.trunc(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return 'вільна';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'вільні';
  return 'вільних';
}

/** Габарити комірки одним рядком: ширина × висота × глибина, см. */
export function cellDimsLabel(info) {
  if (!info) return null;
  const { width, height, depth } = info;
  if (!width || !height || !depth) return null;
  return `${fmtCm(width)}×${fmtCm(height)}×${fmtCm(depth)} см`;
}

function fmtCm(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '?';
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 10) / 10);
}

/**
 * Модель сітки вибору розміру.
 *
 * Без даних (`hasData=false`) вона описує рівно сьогоднішню сітку: пʼять
 * кнопок, жодних кількостей, жодна не погашена.
 */
export function freeCellsView(freeCells) {
  const hasData = Boolean(freeCells && freeCells.available && freeCells.bySize);
  // Після забору знімок частково застарів — див. markFreeCellsStale. Нулі з
  // нього більше не знання, а плюсові числа лишаються чинними (забір комірки
  // тільки звільняє).
  const stale = Boolean(freeCells && freeCells.stale);
  const sizes = CELL_SIZES.map((size) => {
    const info = hasData ? freeCells.bySize[size] : null;
    // Розміру фізично немає (VNA(new), Rozetka): забір цього не змінює, тож
    // такий нуль не знецінюється разом зі знімком.
    const absent = isAbsentSize(info);
    const isStaleZero = stale && Boolean(info) && info.free === 0 && !absent;
    const known = Boolean(info && typeof info.free === 'number') && !isStaleZero;
    const free = known ? info.free : null;
    const total = info && typeof info.total === 'number' ? info.total : null;
    const dims = cellDimsLabel(info);
    let countLabel = '';
    if (absent) countLabel = '—';
    else if (known) countLabel = free === 0 ? 'немає' : `${free} ${freeCellsPlural(free)}`;
    const ariaParts = [size];
    if (absent) ariaParts.push('такого розміру в поштоматі немає');
    else if (known) ariaParts.push(free === 0 ? 'вільних немає' : `${free} ${freeCellsPlural(free)}`);
    if (!absent && total) ariaParts.push(`усього ${total}`);
    if (dims) ariaParts.push(dims);
    return {
      size,
      free,
      total,
      known,
      absent,
      // Гасимо ЛИШЕ явний нуль від 1С. Невідомий розмір лишається робочим.
      disabled: known && free === 0,
      countLabel,
      dims,
      ariaLabel: ariaParts.join(', '),
    };
  });
  // «Вільних немає зовсім» стверджуємо тільки тоді, коли знаємо кожен розмір.
  // Один невідомий — і курʼєру нема чого забороняти.
  const noneFree = hasData && sizes.every((s) => s.known && s.free === 0);
  return {
    hasData,
    stale,
    noneFree,
    actualOn: (freeCells && freeCells.actualOn) || null,
    sizes,
  };
}

/**
 * Чи блокувати спробу цього розміру локально, не ходячи в 1С.
 *
 * Нуль в ОДНОГО розміру блокує: поруч є інші, і спроба дала б лише
 * гарантовану відмову «Немає вільних комірок» (20 336 таких за 14 днів).
 *
 * Нулі в УСІХ — не блокують. Усе, що в нас є, це чужі дані; курʼєр стоїть
 * біля поштомата і бачить його очима, і відібрати в нього останню спробу на
 * такій підставі ми не маємо права. Сказати «вільних немає» зобовʼязані —
 * заборонити ні. Правило живе тут, бо його однаково застосовують чотири
 * місця: сітка і «Відкрити повторно» на кожному з двох поштоматних екранів.
 */
export function isSizeBlocked(view, size) {
  if (!view) return false;
  const item = (view.sizes || []).find((s) => s.size === size);
  // Розміру, якого в поштоматі фізично немає, не відкриє ніхто — навіть коли
  // вільних немає зовсім (тут нема чого лишати курʼєру «на останню спробу»).
  if (item && item.absent) return true;
  if (view.noneFree) return false;
  return Boolean(item && item.disabled);
}

/**
 * Перший розмір, який справді можна відкрити, рахуючи від `from` і ВГОРУ.
 * `null` — вгору немає жодного доступного, а сам `from` погашений нулем.
 *
 * Вниз не йдемо ніколи: посилка, для якої курʼєр узяв S, у XS може не влізти,
 * і підсовувати менший розмір замість більшого — це рішення за курʼєра.
 * Але й повертати розмір, про який 1С щойно сказала «вільних немає», не можна:
 * автовідкриття пішло б із ним у гарантовану відмову, а фокус ліг би на
 * кнопку, яка не натискається.
 */
export function firstOpenableSize(view, from) {
  const sizes = (view && view.sizes) || [];
  const startIdx = sizes.findIndex((s) => s.size === from);
  if (startIdx < 0) return from;
  const found = sizes.slice(startIdx).find((s) => !s.disabled);
  if (found) return found.size;
  return sizes[startIdx].disabled ? null : from;
}

/** Перший доступний розмір у всій сітці — байдуже, в який бік. Або `null`. */
export function anyOpenableSize(view) {
  const found = ((view && view.sizes) || []).find((s) => !s.disabled);
  return found ? found.size : null;
}

/**
 * Локально зменшити лічильник після успішного вкладення.
 *
 * Саме так ми тримаємо обіцяні Meest ≈1 700 викликів на добу: повторно питати
 * 1С після кожного вкладення означало б 12-14 тисяч викликів, увосьмеро більше
 * за обіцяне. А знати нове число ми можемо й самі — розмір комірки нам відомий.
 */
export function decrementFreeCell(freeCells, size) {
  if (!freeCells || !freeCells.available || !freeCells.bySize) return freeCells;
  const info = freeCells.bySize[size];
  if (!info || typeof info.free !== 'number') return freeCells;
  return {
    ...freeCells,
    bySize: { ...freeCells.bySize, [size]: { ...info, free: Math.max(0, info.free - 1) } },
  };
}

/**
 * Позначити розмір як зайнятий після відмови 1С «Немає вільних комірок».
 * Безкоштовне знання: 1С щойно сама це сказала, зайвий виклик не потрібен.
 */
export function markCellSizeEmpty(freeCells, size) {
  if (!freeCells || !freeCells.available || !freeCells.bySize) return freeCells;
  const info = freeCells.bySize[size];
  if (!info) return freeCells;
  if (info.free === 0) return freeCells;
  return {
    ...freeCells,
    bySize: { ...freeCells.bySize, [size]: { ...info, free: 0 } },
  };
}

/**
 * Знімок застарів у бік «стало вільніше»: курʼєр щойно забрав посилку і
 * фізично звільнив комірку, але ЯКОГО розміру — ми не знаємо.
 *
 * Перепитувати 1С не можна (бюджет ≈1 700 викликів на добу — один на візит),
 * а мовчати не можна поготів: знімок знімається ОДРАЗУ після сканування QR,
 * тобто до забору. На повному поштоматі всі пʼять розмірів приходять нулями,
 * курʼєр забирає пʼять посилок — і екран, який вірить тим нулям, каже йому
 * «відкривати нема чого» про поштомат, у якому він сам щойно звільнив місце.
 *
 * Тому після забору нулі перестають бути знанням (розмір знову натискається,
 * плашки «немає вільних комірок» немає), а плюсові числа лишаються: забір
 * комірки їх тільки збільшує, тож як нижня межа вони чинні.
 */
export function markFreeCellsStale(freeCells) {
  if (!freeCells || !freeCells.available || freeCells.stale) return freeCells;
  return { ...freeCells, stale: true };
}

/**
 * Рядок для чипа/тоста: «XS 10 · S 5 · M 4 · L 1 · XL 1», а коли 1С знає склад
 * поштомата (VNA(new), Rozetka) — «вільно/всього»: «S 17/18 · M 6/9 · L 2/3».
 * Розміри, яких у поштоматі фізично немає, не згадуємо.
 */
export function freeCellsSummary(freeCells) {
  if (!freeCells || !freeCells.available || !freeCells.bySize) return null;
  const parts = CELL_SIZES
    .filter((size) => freeCells.bySize[size] && !isAbsentSize(freeCells.bySize[size]))
    .map((size) => {
      const info = freeCells.bySize[size];
      return typeof info.total === 'number' && info.total > 0
        ? `${size} ${info.free}/${info.total}`
        : `${size} ${info.free}`;
    });
  return parts.length ? parts.join(' · ') : null;
}

/** Скільки вільно всього за нашими даними (null, якщо даних немає). */
export function freeCellsTotal(freeCells) {
  if (!freeCells || !freeCells.available || !freeCells.bySize) return null;
  return CELL_SIZES.reduce((sum, size) => {
    const info = freeCells.bySize[size];
    return info && typeof info.free === 'number' ? sum + info.free : sum;
  }, 0);
}

/**
 * Час зрізу даних у вигляді «HH:MM» — рівно той, що назвала 1С.
 *
 * Читаємо години й хвилини прямо з мітки, а не через `new Date().getHours()`:
 * бекенд уже привів час до київського і додав зсув (+02:00/+03:00), і показати
 * його треба як київський. Інакше телефон із чужою зоною (а вони трапляються:
 * роумінг, ручне налаштування) зсунув би підпис на годину-дві, і курʼєр читав
 * би «дані на 13:57» там, де насправді 16:57.
 */
export function freeCellsActualLabel(freeCells) {
  const raw = freeCells && freeCells.actualOn;
  if (!raw) return null;
  const m = /T(\d{2}):(\d{2})/.exec(String(raw));
  return m ? `${m[1]}:${m[2]}` : null;
}
